import { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SiteProvider, useSite } from "./context/SiteContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { FloatingButtons } from "./components/FloatingButtons";
import { GlobalModals } from "./components/GlobalModals";
import { Seo } from "./components/Seo";

const Home = lazy(() => import("./pages/Home"));
const AdminApp = lazy(() => import("./admin/AdminApp"));
const About = lazy(() => import("./pages/About"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const KrishnaVatika = lazy(() => import("./pages/KrishnaVatika"));
const WhyInvest = lazy(() => import("./pages/WhyInvest"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

const Loader = () => (
  <div className="flex min-h-screen items-center justify-center" data-testid="page-loader">
    <span className="font-serif text-2xl text-emerald-800 animate-pulse">M&S</span>
  </div>
);

function GA4() {
  const { settings } = useSite();
  useEffect(() => {
    const id = settings.ga4_id;
    if (!id || document.getElementById("ga4-script")) return;
    const s = document.createElement("script");
    s.id = "ga4-script"; s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", id);
  }, [settings.ga4_id]);
  return null;
}

function PublicLayout() {
  const { refresh } = useSite();
  const { pathname } = useLocation();
  useEffect(() => { refresh(); }, [pathname, refresh]);
  return (
    <div className="App flex min-h-screen flex-col">
      <Seo />
      <GA4 />
      <Header />
      <main className="flex-1">
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/krishna-vatika" element={<KrishnaVatika />} />
            <Route path="/why-invest" element={<WhyInvest />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<LegalPage slug="privacy-policy" />} />
            <Route path="/terms" element={<LegalPage slug="terms" />} />
            <Route path="/disclaimer" element={<LegalPage slug="disclaimer" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <FloatingButtons />
      <GlobalModals />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <SiteProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route path="/admin/*" element={<AdminApp />} />
              <Route path="*" element={<PublicLayout />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </SiteProvider>
    </HelmetProvider>
  );
}

export default App;
